import { QuestionCircleOutlined } from '@ant-design/icons';
import { Modal, Popover, Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import WarningFill from '@/assets/images/icons/bubble-warning.react.svg?react';
import { ENV } from '@/infrastructure/env-config';
import { BASE_NAME, getParamFromUrl } from '@/infrastructure/history';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { stores, useStores } from '@/presentation/store';
import { PropsWithStyles } from '@/proto/common';
import styles from './auth.module.scss';
import { GuideUpgradePageCard } from './guide-upgrade-page';
import { ImageGuideUpgradeCard, ImageGuideUpgradeType } from './image-guide-upgrade';


export enum AuthNodes {
  DeviceWhiteList = 'deviceWhiteList',
  CompareMode = 'compareMode',
  OpenApi = 'openApi',
  WebHook = 'webHook',
  Foom = 'foom',
  OOM = 'oom',
  Launch = 'launch',
  DropFrame = 'dropFrame',
  Memory = 'memory',
  Net = 'net',
  Power = 'power',
  Traffic = 'traffic',
  PagePerfomance = 'pagePerfomance',
  ApplicationExitInfo = 'applicationExitInfo',
  MetricKit = 'metricKit',
  Log = 'log',
  ProSearch = 'proSearch',
  Diff = 'diff',
  WithDropFrame = 'withDropFrame',
  CustomFields = 'customFields',
  ClusterConfig = 'clusterConfig',
  CustomPanel = 'customPanel',
  QualityReport = 'qualityReport',
  Download = 'download',
  LogAnalyze = 'logAnalyze',
  IssueSplit = 'IssueSplit',
  Colpolit = 'colpolit',
  GCDetail = 'gcDetail',
  Scheduling = 'scheduling',
  FDInfo = 'fdInfo',
  ProcessInfo = 'processInfo',
  AnrInfo = 'anrInfo',
  AnrTrace = 'anrTrace',
  Tombstome = 'tombStome',
  LevelUpStrategies = 'levelUpStrategies'
}

type WithMemberAuthorityNodesProps = {
  node: AuthNodes
};

export enum ReportGuideType {
  GuidePage = 'guidePage',
  Bubble = 'bubble',
  Modal = 'modal',
  Card = 'card'
}

export const onUpgrade = (reportProps?: any) => {
  const url = new URL(window.location.origin);
  reportClick(reportProps.reportScene, CUSTOM_ACTIONS.guideUpgradeClick, {
    from: reportProps.pgid,
    param3: reportProps.reportGuideType,
    param4: reportProps.reportField,
  });
  const productId = getParamFromUrl('productId');
  url.pathname = `${BASE_NAME}/resource-pack`;
  if (productId) {
    url.searchParams.set('appId', productId);
  }
  window.open(url.toString(), '_blank');
};

// @ts-ignore
export const WithMemberAuthorityNodes: React.FC<WithMemberAuthorityNodesProps> = props => props.children;

type AuthFilterList = {
  node?: AuthNodes;
  [key: string]: any
};

export const withAuthFilter = (filterList: AuthFilterList[]): any => {
  const { authorityNodes = [] } = stores.ProductStore;
  return filterList?.filter(filter => !filter.node || authorityNodes.includes(filter.node));
};

export interface WithProUpgradeProps {
  desc?: any;
  placement?: TooltipPlacement;
  reportGuideType?: ReportGuideType
  reportScene?: string
  reportField?: string
}

// @ts-ignore
export const WithProUpgrade: FC<WithProUpgradeProps> = (props) => {
  const { t } = useTranslation();
  const { reportGuideType, reportScene, reportField } = props;
  const { isFreeProduct } = useStores().ProductStore;
  const { pgid } = useStores().LayoutStore;
  const reportProps = { reportScene, pgid, reportGuideType, reportField };

  const isFree = isFreeProduct();

  const desc = props.desc || t('专业版支持更多功能');
  const PRO_UPGRADE_TIPS = (
    <span>{desc}{t('，')}<a onClick={() => onUpgrade(reportProps)}>{t('去升级')}</a></span>
  );

  if (isFree) {
    return (
      <Tooltip overlay={PRO_UPGRADE_TIPS} placement={props.placement}>
        {props.children}
      </Tooltip>
    );
  }

  return props.children;
};
// 左侧导航升级引导
// @ts-ignore
export const RouteWithProUpgrade: FC<PropsWithStyles<WithProUpgradeProps>> = (props) => {
  const { isFreeProduct } = useStores().ProductStore;

  const isFree = isFreeProduct();
  const { reportGuideType, reportScene, reportField } = props;
  if (isFree) {
    return (
      <GuideUpgradePageCard
        style={props.style}
        reportGuideType={reportGuideType}
        reportScene={reportScene}
        reportField={reportField}
      />
    );
  }

  return (
    props.children
  );
};

// 气泡升级引导
// @ts-ignore
export const BubbleWithProUpgrade: FC<WithProUpgradeProps> = (props) => {
  const { t } = useTranslation();
  const { reportScene, reportGuideType, reportField } = props;
  const { pgid } = useStores().LayoutStore;
  const { isFreeProduct } = useStores().ProductStore;

  // woa版和Internal版直接返回不判断
  if (ENV.isWOA || ENV.isInternal) {
    return props.children;
  }

  const isFree = isFreeProduct();
  const reportProps = { reportScene, pgid, reportGuideType, reportField };

  const desc = props.desc || <span>{t('体验产品不支持此功能，请')}<a onClick={() => onUpgrade(reportProps)}>{t('购买资源包')}</a>{t('，升级为付费产品后再使用。')}</span>;
  const AUTH_GUIDE_TIPS = (
    <div style={{ display: 'flex' }}>
      <WarningFill />
      <span style={{ paddingLeft: '8px' }}>
        {desc}
      </span>
    </div>
  );

  if (isFree) {
    return (
      <Popover content={AUTH_GUIDE_TIPS} trigger="hover" placement={props.placement} onVisibleChange={(visible) => {
        if (visible) {
          reportClick(reportScene!, CUSTOM_ACTIONS.guideUpgradeClick, {
            from: pgid,
            param3: props.reportGuideType,
            param4: props.reportField,
          });
        }
      }}>
        <div style={{ cursor: 'not-allowed' }}>
          <div style={{ pointerEvents: 'none', opacity: 0.5 }}>{props.children}</div>
        </div>
      </Popover>
    );
  }

  return props.children;
};
// 图卡升级引导
// @ts-ignore
export const WithImageGuideUpgrade: FC<WithProUpgradeProps> = (props) => {
  const { isFreeProduct } = useStores().ProductStore;


  const isFree = isFreeProduct();

  if (isFree) {
    return (
      <ImageGuideUpgradeCard
        type={ImageGuideUpgradeType.disableAny}
        reportGuideType={props.reportGuideType}
        reportScene={props.reportScene}
        reportField={props.reportField}
      />
    );
  }

  return props.children;
};
// tab引导升级
// @ts-ignore
export const WithProUpgradeTab: FC<WithProUpgradeProps> = (props) => {
  const { isFreeProduct } = useStores().ProductStore;

  const isFree = isFreeProduct();

  if (isFree) {
    return (
      <span>{props.children} <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px' }} /></span>
    );
  }

  return props.children;
};

// 弹窗引导升级
export const WithModalGuideUpgrade = (props: {
  desc?: ReactNode;
  okText?: string;
  cancelText?: string;
  reportScene?: string;
  reportGuideType?: string;
  reportField?: string;
}) => {
  const { t } = useTranslation();
  const { reportScene, reportField, reportGuideType } = props;
  const { pgid } = useStores().LayoutStore;
  const reportProps = { reportScene, pgid, reportGuideType, reportField };
  const displayDesc = props.desc || (
    <span>
      {t('体验产品不支持此功能，请')}<a onClick={() => onUpgrade(reportProps)}>{t('购买资源包')}</a>
      {t('，升级为付费产品后再使用。')}
    </span>
  );
  const displayOkText = props.okText || t('好的');
  const displayCancelText = props.cancelText || t('取消');

  const content = <div>{displayDesc}</div>;

  return Modal.info({
    icon: <></>,
    content,
    okText: displayOkText,
    cancelText: displayCancelText,
    onCancel: () => onUpgrade(reportProps),
    closable: true,
    wrapClassName: styles.modal,
  });
};


// 按钮升级引导
// @ts-ignore
export const WithProUpgradeButton: FC<WithProUpgradeProps> = (props) => {
  const { t } = useTranslation();
  const { isNormalProduct } = useStores().ProductStore;

  // woa版和Internal版直接返回不判断
  if (ENV.isWOA || ENV.isInternal) {
    return props.children;
  }

  const isNormal = isNormalProduct();

  const desc = props.desc || <span>{t('当前产品未绑定有效资源包，无法修改配置。')}</span>;
  const AUTH_GUIDE_TIPS = (
    <div style={{ display: 'flex', paddingTop: '2px', paddingBottom: '2px', paddingLeft: '8px', paddingRight: '8px' }}>
      <WarningFill />
      <span style={{ paddingLeft: '8px', color: 'black' }}>
        {desc}
      </span>
    </div>
  );

  if (isNormal) {
    return (
      <Tooltip
        placement={props.placement}
        title={AUTH_GUIDE_TIPS}
        arrowPointAtCenter={true}
        trigger="hover"
        autoAdjustOverflow={true}
        color={'white'}
        overlayInnerStyle={{ borderRadius: '4px' }}
        overlayStyle={{ maxWidth: '800px' }}>
        {React.Children.map(props.children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement<any>(child, {
              disabled: true,
              onClick: () => { },
              style: {
                ...child.props.style,
                opacity: 0.5,
                color: 'gray',
                cursor: 'not-allowed',
                textDecoration: 'none',
              },
            });
          }
          return null;
        })}
      </Tooltip>
    );
  }

  return props.children;
};

